import * as UI from "@iyk/music-ui"
import { ServerErrorData } from "@iyk/react-router/client"
import { AppResponse, HttpRequest } from "@iyk/react-router/server"
import { Effect } from "effect"
import React from "react"
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router"
import type { Route } from "./+types/root"
import stylesheet from "./app.css?url"
import { AppRoutes } from "./AppRoutes.ts"
import { AppHandler } from "./server/AppHandler.ts"
import { Meta as MetaTags } from "./ui/components/Meta.tsx"

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "apple-touch-icon", sizes: "180x180", href: UI.Assets.images.AppleTouchIconPngUrl },
  { rel: "icon", type: "image/x-icon", href: UI.Assets.images.FaviconIcoUrl },
  { rel: "icon", sizes: "any", type: "image/svg+xml", href: UI.Assets.images.FaviconSvgUrl }
]

export const loader = AppHandler.loader("root")(HttpRequest.get.build()).handle((_, context) => {
  return Effect.gen(function* () {
    const toasts = yield* context.toasts.flash
    return AppResponse.Json({ toasts })
  })
})

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="dark bg-gray-1 text-gray-12 min-w-0">
        <UI.MusicProviders>{children}</UI.MusicProviders>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App({ loaderData }: Route.ComponentProps) {
  const toast = UI.useToast()

  React.useEffect(() => {
    for (const message of loaderData.toasts) {
      toast.add({
        title: message.title,
        description: message.description,
        type: message.type
      })
    }
  }, [loaderData.toasts])

  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Unexpected error"
  let details = "Something went wrong on our end. Try refreshing the page, or going back to home."
  let stack: string | undefined
  let traceId: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "Page not found" : "Error"
    details = error.status === 404 ? "We can't find the page you're looking for" : error.statusText || details
    if (error.status === 500) {
      traceId = ServerErrorData.fromUnknown(error.data)?.traceId
    }
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  return (
    <>
      <MetaTags title={message} />
      <UI.ErrorBoundary
        message={message}
        details={details}
        stack={stack}
        traceId={traceId}
        route={AppRoutes.worlds.index}
      />
    </>
  )
}
